/**
 * Get the API base URL for Polyglot endpoints.
 * Uses /++api++ prefix which nginx routes to Nick.
 */
export function getApiPath() {
  return '/++api++';
}

/**
 * Get auth headers for API requests.
 * Reads the auth_token cookie (set by Volto on login) and returns
 * an Authorization header for Nick's JWT verification.
 *
 * Returns empty object if no token (SSR or not logged in),
 * so fetch() calls work without conditional logic.
 */
export function getAuthHeaders() {
  if (typeof document === 'undefined') return {};
  const match = document.cookie.match(
    /(?:^|;\s*)auth_token=([^;]+)/,
  );
  return match ? { Authorization: `Bearer ${match[1]}` } : {};
}

/**
 * DR-2026-053: Get current user ID from JWT cookie payload.
 * Returns null on SSR or if no token present.
 */
export function getCurrentUserId() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)auth_token=([^;]+)/);
  if (!match) return null;
  try {
    const payload = JSON.parse(atob(match[1].split('.')[1]));
    return payload.id || payload.sub || null;
  } catch {
    return null;
  }
}

/**
 * DR-2026-053: Check if current user is restricted from terminology access.
 */
const TERMINOLOGY_RESTRICTED_USERS = new Set(['bucharest26']);
export function isTerminologyRestricted() {
  return TERMINOLOGY_RESTRICTED_USERS.has(getCurrentUserId());
}

/**
 * Fetch wrapper with auth headers and response handling.
 * - Adds Authorization header from auth_token cookie
 * - On 401: redirects to /login with return_url
 * - On other errors: throws with status text
 * - On success: returns parsed JSON
 */
export async function fetchApi(url, options) {
  const res = await fetch(url, { ...options, headers: { ...getAuthHeaders(), ...(options?.headers || {}) } });
  if (res.status === 401) {
    // If user has a token, this is a permission denial, not an expired session.
    // Don't redirect to login — that causes an infinite loop.
    const hasToken = typeof document !== 'undefined' && document.cookie.includes('auth_token=');
    if (!hasToken && typeof window !== 'undefined') {
      window.location.href = `/login?return_url=${encodeURIComponent(window.location.pathname)}`;
    }
    throw new Error(hasToken ? 'You do not have permission to access this resource.' : 'Session expired');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `${res.status} ${res.statusText}`);
  }
  return res.json();
}
